import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs';
import { AreaService, JobTitleService } from 'src/app/api/services';
import { BusinessLineService } from 'src/app/api/services/business-line.service';
import { UserExperienceService } from 'src/app/api/services/user-experience.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { InputDateComponent } from 'src/app/shared/component/input-date/input-date.component';
import { InputSelectComponent } from 'src/app/shared/component/input-select/input-select.component';
import { InputTextComponent } from 'src/app/shared/component/input-text/input-text.component';
import { InputTextareaComponent } from 'src/app/shared/component/input-textarea/input-textarea.component';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { BaseFormComponent } from 'src/app/shared/form/base-form.component';
import { SelectOption } from 'src/app/shared/model/select-option';
import { ToastService } from 'src/app/shared/service/toast.service';
import { dateRangeOrderValidator } from 'src/app/shared/validator/date-range-order.validator';
import { dateValidator } from 'src/app/shared/validator/dateValidator';

@Component({
  selector: 'app-update-experience',
  imports: [
    CommonModule,
    InputTextComponent,
    InputSelectComponent,
    LoadingButtonComponent,
    ReactiveFormsModule,
    FormsModule,
    InputDateComponent,
    InputTextareaComponent
  ],
  templateUrl: './update-experience.component.html',
  styles: ``,
})
export class UpdateExperinceComponent
  extends BaseFormComponent
  implements OnInit, OnChanges
{
  @Input() experience: UserExperienceResponse | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<UserExperienceResponse>();

  experienceForm!: FormGroup;
  loading: boolean = false;

  bussinessLines: SelectOption[] = [];
  jobTitles: SelectOption[] = [];
  areas: SelectOption[] = [];

  constructor(
    private fb: FormBuilder,
    private userExpService: UserExperienceService,
    private jobTitleService: JobTitleService,
    private bussinessLineService: BusinessLineService,
    private areaService: AreaService,
    toastService: ToastService
  ) {
    super(toastService);
  }

  ngOnInit(): void {
    this.loadCatalogs();
  }

  ngOnChanges(): void {
    if (this.experience) {
      this.buildForm();
    }
  }

  private buildForm(): void {
    const ref = this.experience?.references?.[0];

    this.experienceForm = this.fb.group(
      {
        idJobTitle: [this.experience?.idJobTitle, Validators.required],
        idBusinessLine: [this.experience?.idBusinessLine, Validators.required],
        idArea: [this.experience?.idArea, Validators.required],
        companyName: [this.experience?.companyName, Validators.required],
        functionsDescription: [
          this.experience?.functionsDescription,
          Validators.required,
        ],
        fromProperty: [
          this.formatDate(this.experience?.fromProperty),
          [Validators.required, dateValidator()],
        ],
        until: [
          this.formatDate(this.experience?.until),
          [Validators.required, dateValidator()],
        ],
        references: this.fb.array([
          this.fb.group({
            fullNameContact: [ref?.fullNameContact, Validators.required],
            telfContact: [
              ref?.telfContact,
              [
                Validators.required,
                Validators.minLength(9),
                Validators.maxLength(9),
                Validators.pattern(/^[0-9]+$/),
              ],
            ],
          }),
        ]),
      },
      { validators: [dateRangeOrderValidator('fromProperty', 'until')] }
    );
  }

  private loadCatalogs(): void {
    this.jobTitleService.tituloEmpleoGet().subscribe({
      next: (d) =>
        (this.jobTitles = d.dto.jobTitles.map((i: any) => ({
          value: i.idJobTitle,
          label: i.name,
        }))),
      error: (e) => this.showApiError(e, MESSAGES.JOB_TITLE.GET_ALL_ERROR),
    });

    this.bussinessLineService.getBusinessLines().subscribe({
      next: (d) =>
        (this.bussinessLines = d.dto.businessLines.map((i: any) => ({
          value: i.idBusinessLine,
          label: i.description,
        }))),
      error: (e) => this.showApiError(e, MESSAGES.BUSSINES_LINE.GET_ALL_ERROR),
    });

    this.areaService.areaGet().subscribe({
      next: (d) =>
        (this.areas = d.dto.areas.map((i: any) => ({
          value: i.idArea,
          label: i.name,
        }))),
      error: (e) => this.showApiError(e, MESSAGES.AREA.GET_ALL_ERROR),
    });
  }

  onUpdateClick(): void {
    if (!this.validateForm(this.experienceForm)) return;

    const payload = {
      ...this.experienceForm.value,
      fromProperty: this.toBackendDate(this.experienceForm.value.fromProperty),
      until: this.toBackendDate(this.experienceForm.value.until),
    };

    this.loading = true;
    this.userExpService
      .updateExperience(this.experience?.idUserExperience || '', payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.toastService.showMessages('success', [
            MESSAGES.USER.EXPERIENCE_UPDATE_SUCCESS,
          ]);
          this.save.emit({ ...this.experience!, ...payload });
          this.close.emit();
        },
        error: (err) =>
          this.showApiError(err, MESSAGES.USER.EXPERIENCE_UPDATE_ERROR),
      });
  }

  onCloseClick() {
    this.close.emit();
  }

  get references(): FormArray {
    return this.experienceForm.get('references') as FormArray;
  }

  private toBackendDate(value: string): string | null {
    if (!value) return null;
    const [d, m, y] = value.split('/');
    return `${y}-${m}-${d}`;
  }

  private formatDate(value?: string | Date): string {
    if (!value) return '';
    const d = new Date(value);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }
}
